import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { PropertySchemaAssociationInput, ValidationError } from "~/common/types";
import {
  CreateDataSourceTemplateDocument,
  type CreateDataSourceTemplateMutation,
  type CreateDataSourceTemplateMutationVariables,
  GetDataSourceTemplatesDocument,
} from "~/features/dataSourceTemplates/gqls";
import type { DataSourceTemplateFormData } from "~/features/dataSourceTemplates/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseDataSourceTemplateCreateOptions {
  onSuccess?: (data: DataSourceTemplateFormData) => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

export function useDataSourceTemplateCreate({ onSuccess, onError }: UseDataSourceTemplateCreateOptions = {}) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [createDataSourceTemplate] = useMutation<
    CreateDataSourceTemplateMutation,
    CreateDataSourceTemplateMutationVariables
  >(CreateDataSourceTemplateDocument);

  const submit = useCallback(
    async (formData: DataSourceTemplateFormData) => {
      setProcessing(true);
      try {
        const res = await createDataSourceTemplate({
          refetchQueries: [{ query: GetDataSourceTemplatesDocument, variables: { tenantId } }],
          variables: {
            input: {
              tenantId,
              name: formData.name,
              propertySchemaAssociations: (formData.propertySchemaAssociations ??
                []) as unknown as PropertySchemaAssociationInput[],
            },
          },
        });

        match(res.data?.cnutCreateDataSourceTemplate)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "DataSourceTemplateCreateOutput" }, () => {
            toast.success(t("feature.common.createdSuccessfully", { model: "dataSourceTemplate" }));
            onSuccess?.(formData);
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [createDataSourceTemplate, tenantId, t, onSuccess, onError],
  );

  return { submit, processing };
}
